<template>
  <!--
    科室花费明细（独立页面）
    - 统计与导出全部由后端接口完成，前端只做展示：不拉全量工单、不做前端聚合。
    - 布局：用自定义类名 .repair-dept-stats-page，不用全局 .app-container
      （Home.vue 的 .app-container 是 100vh + flex 的外壳规则，命中子页面会把 el-table 压扁、行被无声裁掉）。
  -->
  <div class="repair-dept-stats-page">
    <el-card class="box-card" shadow="never">
      <div slot="header" class="clearfix header-bar">
        <span class="page-title">科室花费明细</span>
        <div class="header-actions">
          <el-button size="small" icon="el-icon-download" :loading="exporting" @click="handleExport">
            导出Excel
          </el-button>
          <el-button size="small" icon="el-icon-refresh-right" :loading="loading" @click="fetchStats">刷新</el-button>
        </div>
      </div>

      <!-- 查询区：label 13px、控件 size=small（与 RepairStats.vue / RepairList.vue 保持一致） -->
      <el-form :inline="true" :model="queryForm" size="small" class="search-form" @submit.native.prevent>
        <el-form-item label="报修日期">
          <el-date-picker
            v-model="queryForm.dateRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            clearable
            style="width: 250px"
          />
        </el-form-item>
        <el-form-item label="工单状态">
          <el-select v-model="queryForm.status" placeholder="全部" clearable style="width: 130px">
            <el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="科室">
          <!-- department-select 的 v-model 是「科室名称字符串」，不是 id；组件已内置拼音搜索 -->
          <department-select v-model="queryForm.department" placeholder="全部科室" style="width: 200px" />
        </el-form-item>
        <el-form-item label="关键词">
          <el-input
            v-model="queryForm.keyword"
            placeholder="工单号 / 设备名称 / 故障描述"
            clearable
            style="width: 200px"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item class="search-buttons">
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 说明条：口径 + 花费定义 + 展开用法 -->
      <el-alert
        type="info"
        :closable="false"
        show-icon
        class="legend-alert"
        title="按设备归属科室聚合；花费＝工单配件金额（数量×单价）合计；点行首箭头展开该科室工单明细。"
      />

      <!-- 汇总表：数据直接来自后端聚合接口，科室数量不多，不做前端分页 -->
      <el-table
        v-loading="loading"
        :data="rows"
        border
        size="small"
        row-key="department"
        :expand-row-keys="expandedKeys"
        class="dept-table"
        @expand-change="handleExpandChange"
      >
        <el-table-column type="index" :index="rowIndex" label="序号" width="55" align="center" />
        <el-table-column type="expand">
          <template slot-scope="scope">
            <!-- 展开区：首次展开才请求该科室明细，结果按科室缓存，避免重复请求 -->
            <div v-loading="detailState(scope.row).loading" class="dept-detail-wrap">
              <div v-if="detailState(scope.row).error" class="detail-error">
                <i class="el-icon-warning-outline" />
                <span>{{ detailState(scope.row).error }}</span>
                <el-button type="text" size="mini" icon="el-icon-refresh" @click="retryDetail(scope.row)">重试</el-button>
              </div>
              <template v-else>
                <el-table :data="pagedDetail(scope.row)" border size="small" class="dept-detail-table">
                  <el-table-column label="#" width="50" align="center">
                    <!-- 序号带上前端分页偏移：用模板算，避免依赖 el-table-column index 回调的签名差异 -->
                    <template slot-scope="s">
                      {{ (detailState(scope.row).page - 1) * detailPageSize + s.$index + 1 }}
                    </template>
                  </el-table-column>
                  <el-table-column prop="order_no" label="工单号" width="150" show-overflow-tooltip />
                  <el-table-column label="设备名称" min-width="150" show-overflow-tooltip>
                    <template slot-scope="s">
                      <span>{{ s.row.equipment_name || '-' }}</span>
                      <el-tag v-if="s.row.is_emergency" type="danger" size="mini" class="mark-tag">急救</el-tag>
                      <el-tag v-else-if="s.row.is_urgent" type="warning" size="mini" class="mark-tag">加急</el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column label="型号" width="130" show-overflow-tooltip>
                    <template slot-scope="s">{{ s.row.equipment_model || '-' }}</template>
                  </el-table-column>
                  <el-table-column label="故障描述" min-width="200" show-overflow-tooltip>
                    <template slot-scope="s">{{ s.row.fault_desc || '-' }}</template>
                  </el-table-column>
                  <el-table-column label="维修结果" width="110" align="center">
                    <template slot-scope="s">{{ s.row.repair_result_display || '-' }}</template>
                  </el-table-column>
                  <el-table-column label="配件明细" min-width="180" show-overflow-tooltip>
                    <template slot-scope="s">{{ s.row.parts_text || '无配件' }}</template>
                  </el-table-column>
                  <el-table-column label="花费" width="100" align="right">
                    <template slot-scope="s">¥{{ money(s.row.cost) }}</template>
                  </el-table-column>
                  <el-table-column label="状态" width="100" align="center">
                    <template slot-scope="s">
                      <el-tag :type="statusTagType(s.row.status)" size="small">{{ statusText(s.row) }}</el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column label="报修时间" width="150" align="center">
                    <template slot-scope="s">{{ s.row.created_at || '-' }}</template>
                  </el-table-column>
                </el-table>

                <div class="detail-foot">
                  <span>共 {{ detailState(scope.row).list.length }} 条工单</span>
                  <!-- 明细超过 50 条时加简单前端分页，避免展开区被拉得过长 -->
                  <el-pagination
                    v-if="detailState(scope.row).list.length > 50"
                    class="detail-pager"
                    small
                    background
                    layout="prev, pager, next"
                    :total="detailState(scope.row).list.length"
                    :page-size="detailPageSize"
                    :current-page="detailState(scope.row).page"
                    @current-change="p => handleDetailPageChange(scope.row, p)"
                  />
                </div>
              </template>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="department" label="科室名称" min-width="150" show-overflow-tooltip />
        <el-table-column prop="total" label="报修数量" width="100" align="center" />
        <el-table-column prop="doing" label="在途" width="80" align="center" />
        <el-table-column prop="done" label="已完成" width="90" align="center" />
        <el-table-column prop="canceled" label="已撤销" width="90" align="center" />
        <el-table-column prop="emergency" label="急救设备" width="100" align="center" />
        <el-table-column label="花费合计" width="120" align="right">
          <template slot-scope="scope">¥{{ money(scope.row.cost) }}</template>
        </el-table-column>
        <template slot="empty">
          <span v-if="backendError">后端未启动或接口异常，错误信息已弹窗提示</span>
          <span v-else>当前筛选条件下暂无报修记录</span>
        </template>
      </el-table>

      <!-- 底部合计条：优先用后端 summary，字段缺失时按 rows 兜底（后端并行开发中） -->
      <div v-if="rows.length" class="dept-summary">
        <span>科室数：<b>{{ deptCount }}</b></span>
        <span>工单合计：<b>{{ orderTotal }}</b> 条</span>
        <span>花费合计：<b class="sum-money">¥{{ money(costTotal) }}</b></span>
      </div>
    </el-card>
  </div>
</template>

<script>
import repairApi from '../api/repair'
import DepartmentSelect from './DepartmentSelect.vue'

// 工单状态枚举（与后端/报修列表页保持一致）
const STATUS_OPTIONS = [
  { value: 'PENDING', label: '待接单' },
  { value: 'PROCESSING', label: '维修中' },
  { value: 'SUSPENDED', label: '挂起' },
  { value: 'WAIT_SIGN', label: '待签字' },
  { value: 'SIGNED', label: '已关闭' },
  { value: 'CANCELLED', label: '已撤销' },
]
const STATUS_TEXT = {
  PENDING: '待接单',
  PROCESSING: '维修中',
  SUSPENDED: '挂起',
  WAIT_SIGN: '待签字',
  SIGNED: '已关闭',
  CANCELLED: '已撤销',
}
// 单科室明细拉取：每页条数与最大翻页数（防止后端 count 异常时死循环）
const DETAIL_PAGE_SIZE = 200
const DETAIL_MAX_PAGE = 20

export default {
  name: 'RepairDeptStats',
  components: { DepartmentSelect },
  data() {
    return {
      rows: [], // 后端聚合结果 rows（前端不做任何聚合）
      summary: {}, // 后端 summary：{ dept_count, order_total, cost_total }
      loading: false,
      exporting: false,
      backendError: false,
      statusOptions: STATUS_OPTIONS,
      queryForm: {
        dateRange: ['', ''],
        status: '',
        department: '',
        keyword: '',
      },
      // 受控展开行：row-key 用科室名，查询条件变化时整体清空
      expandedKeys: [],
      // 明细缓存：{ [科室名]: { list, total, page, loading, loaded, error } }
      detailCache: {},
      detailPageSize: 50, // 展开区前端分页每页条数
    }
  },
  computed: {
    // 科室数：后端 summary 优先，缺失则按 rows 行数
    deptCount() {
      return this.summary.dept_count != null ? this.summary.dept_count : this.rows.length
    },
    // 工单合计
    orderTotal() {
      if (this.summary.order_total != null) return this.summary.order_total
      return this.rows.reduce((s, r) => s + (Number(r.total) || 0), 0)
    },
    // 花费合计
    costTotal() {
      if (this.summary.cost_total != null) return this.summary.cost_total
      return this.rows.reduce((s, r) => s + (Number(r.cost) || 0), 0)
    },
  },
  created() {
    this.fetchStats()
  },
  methods: {
    // 汇总：GET /api/mp/repair/stats/by-dept/
    async fetchStats() {
      this.loading = true
      this.backendError = false
      try {
        const res = await repairApi.getDeptStats(this.buildParams())
        const data = res.data || {}
        this.rows = Array.isArray(data.rows) ? data.rows : []
        this.summary = data.summary || {}
        // 条件变了：明细缓存与展开状态一起失效，避免看到上一次的旧明细
        this.detailCache = {}
        this.expandedKeys = []
      } catch (e) {
        // 失败不静默：把后端的 detail / 字段错误直接透出
        this.backendError = true
        this.rows = []
        this.summary = {}
        this.detailCache = {}
        this.expandedKeys = []
        this.$message.error(this.errorText(e))
      } finally {
        this.loading = false
      }
    },
    // 查询：重置页码（展开区明细回到第 1 页）后重新拉汇总
    handleSearch() {
      this.resetDetailPages()
      this.fetchStats()
    },
    handleReset() {
      this.queryForm = { dateRange: ['', ''], status: '', department: '', keyword: '' }
      this.resetDetailPages()
      this.fetchStats()
    },
    // 汇总接口参数：start_date / end_date / status / department / keyword，空值不下发
    buildParams() {
      const [start, end] = this.queryForm.dateRange || []
      const params = {}
      if (start) params.start_date = start
      if (end) params.end_date = end
      if (this.queryForm.status) params.status = this.queryForm.status
      if (this.queryForm.department) params.department = this.queryForm.department
      if (this.queryForm.keyword) params.keyword = this.queryForm.keyword
      return params
    },
    // 明细接口参数：department 必填，其余筛选条件同汇总
    buildDetailParams(department) {
      return Object.assign({ department }, this.buildParams())
    },
    // 展开/折叠：展开时才拉明细，结果按科室缓存（折叠再展开不会重复请求）
    handleExpandChange(row, expandedRows) {
      const keys = (expandedRows || []).map(r => r.department)
      this.expandedKeys = keys
      if (keys.indexOf(row.department) === -1) return // 折叠：无需请求
      this.loadDetail(row)
    },
    // 取某科室的明细缓存对象；未创建时返回只读占位（渲染时不会报错）
    detailState(row) {
      return (
        this.detailCache[row.department] || {
          list: [],
          total: 0,
          page: 1,
          loading: false,
          loaded: false,
          error: '',
        }
      )
    },
    async loadDetail(row) {
      const dept = row.department
      if (!this.detailCache[dept]) {
        this.$set(this.detailCache, dept, {
          list: [],
          total: 0,
          page: 1,
          loading: false,
          loaded: false,
          error: '',
        })
      }
      const cache = this.detailCache[dept]
      if (cache.loaded || cache.loading) return // 已缓存或正在拉，不重复请求
      cache.loading = true
      cache.error = ''
      try {
        const list = []
        let page = 1
        let total = Infinity
        // 单科室工单量有限：按页拉齐 count 条即可（后端分页，越界回退末页）
        while ((page - 1) * DETAIL_PAGE_SIZE < total && page <= DETAIL_MAX_PAGE) {
          const res = await repairApi.getDeptStatsDetail(
            Object.assign(this.buildDetailParams(dept), {
              page,
              page_size: DETAIL_PAGE_SIZE,
            })
          )
          const data = res.data || {}
          const part = Array.isArray(data.results) ? data.results : []
          list.push(...part)
          total = data.count != null ? data.count : part.length
          if (!part.length) break
          page += 1
        }
        cache.list = list
        cache.total = total
        cache.loaded = true
      } catch (e) {
        // 失败不静默：提示里带后端真实错误，展开区同时给「重试」入口
        cache.error = this.errorText(e)
        this.$message.error(`「${dept}」工单明细加载失败：${cache.error}`)
      } finally {
        cache.loading = false
      }
    },
    // 重试：丢弃缓存后重新拉
    retryDetail(row) {
      this.$delete(this.detailCache, row.department)
      this.loadDetail(row)
    },
    // 展开区前端分页：明细超过 50 条时才出现翻页
    pagedDetail(row) {
      const cache = this.detailState(row)
      const start = (cache.page - 1) * this.detailPageSize
      return cache.list.slice(start, start + this.detailPageSize)
    },
    handleDetailPageChange(row, page) {
      const cache = this.detailCache[row.department]
      if (cache) cache.page = page
    },
    // 查询/重置时把各科室明细翻回第 1 页
    resetDetailPages() {
      Object.keys(this.detailCache).forEach(k => {
        this.detailCache[k].page = 1
      })
    },
    rowIndex(idx) {
      return idx + 1
    },
    // 导出：后端生成 xlsx，前端只负责把 blob 落盘（不用 SheetJS，避免非手势下载被浏览器拦截）
    async handleExport() {
      if (!this.rows.length) {
        this.$message.warning('当前没有可导出的科室统计')
        return
      }
      this.exporting = true
      try {
        const res = await repairApi.exportDeptStats(this.buildParams())
        const blob = res.data
        // 兜底：后端若以 JSON 形式返回错误（blob 类型仍是 application/json），转成文字抛出来
        if (blob && blob.type && blob.type.indexOf('application/json') !== -1) {
          throw new Error(await this.blobToText(blob))
        }
        const url = window.URL.createObjectURL(new Blob([blob]))
        const a = document.createElement('a')
        a.href = url
        a.download = this.pickFileName(res)
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      } catch (e) {
        this.$message.error('导出失败：' + this.errorText(e))
      } finally {
        this.exporting = false
      }
    },
    // 读取 blob 文本（用于把后端 JSON 报错转成提示文案）
    blobToText(blob) {
      return new Promise(resolve => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () => resolve('')
        reader.readAsText(blob)
      })
    },
    // 文件名：优先 Content-Disposition 的 filename*=UTF-8''（RFC 5987），取不到再回落
    pickFileName(res) {
      const headers = (res && res.headers) || {}
      const raw = headers['content-disposition'] || headers['Content-Disposition'] || ''
      const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(raw)
      if (star) {
        try {
          return decodeURIComponent(star[1].replace(/"/g, '').trim())
        } catch (e) {
          // 解码失败则继续走下面的回落
        }
      }
      const plain = /filename\s*=\s*"([^"]+)"/i.exec(raw) || /filename\s*=\s*([^;]+)/i.exec(raw)
      if (plain) return plain[1].trim()
      return `报修科室花费统计_${this.formatDateForFile(new Date())}.xlsx`
    },
    formatDateForFile(d) {
      const p = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`
    },
    // 从异常里提取可读信息：优先后端 detail / 字段错误，其次 HTTP 状态行，最后 error.message
    errorText(e) {
      const resp = e && e.response && e.response.data
      if (resp) {
        if (typeof resp === 'string') return resp
        if (resp.detail) return String(resp.detail)
        const firstKey = Object.keys(resp)[0]
        if (firstKey) {
          const v = resp[firstKey]
          return `${firstKey}：${Array.isArray(v) ? v.join('；') : String(v)}`
        }
      }
      if (e && e.response && e.response.status) return `请求失败（HTTP ${e.response.status}）`
      return (e && e.message) || '未知错误'
    },
    money(v) {
      return (Number(v) || 0).toFixed(2)
    },
    statusText(row) {
      return (row && row.status_display) || STATUS_TEXT[row.status] || '-'
    },
    statusTagType(status) {
      const map = {
        PENDING: 'warning',
        PROCESSING: 'primary',
        SUSPENDED: 'info',
        WAIT_SIGN: 'warning',
        SIGNED: 'success',
        CANCELLED: 'info',
      }
      return map[status] || 'info'
    },
  },
}
</script>

<style scoped>
.repair-dept-stats-page {
  padding: 16px;
}
.box-card {
  width: 100%;
}
.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.page-title {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}
.header-actions {
  display: flex;
  gap: 8px;
}
/* 查询区：label 13px、控件 13px/高 32px，与报修列表页统一 */
.search-form /deep/ .el-form-item__label {
  font-weight: bold;
  font-size: 13px;
}
.search-form /deep/ .el-input__inner {
  font-size: 13px;
  height: 32px;
  line-height: 32px;
  color: #303133;
}
.search-form /deep/ .el-input__inner::placeholder {
  font-size: 13px;
}
.search-form /deep/ .el-input__icon {
  line-height: 32px;
}
.search-form /deep/ .el-range-editor.el-input__inner {
  padding: 0 8px;
}
.search-form /deep/ .el-date-editor .el-range-separator {
  line-height: 26px;
}
.search-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 10px 12px 0 12px;
}
.search-form .el-form-item {
  margin-bottom: 10px;
}
.search-buttons {
  margin-left: auto;
}
.legend-alert {
  margin-bottom: 12px;
}
.dept-table {
  margin-top: 0;
}
.mark-tag {
  margin-left: 4px;
}
/* 展开区：明细表内嵌，与外层表格视觉分离 */
.dept-detail-wrap {
  padding: 8px 12px;
  background-color: #fffdf8;
}
.dept-detail-table {
  width: 100%;
}
.detail-error {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #f56c6c;
}
.detail-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}
.detail-pager {
  padding: 0;
}
.dept-summary {
  margin-top: 12px;
  font-size: 13px;
  color: #606266;
  display: flex;
  gap: 20px;
}
.dept-summary b {
  color: #303133;
  font-size: 14px;
}
.sum-money {
  color: #e6a23c !important;
}
</style>
